import React, { useEffect, useRef } from "react";
import {
  TimeParts,
  TimePickerMenuPosition,
  TimePickerSize,
} from "./timePicker.types";
import {
  Column,
  ColumnScroll,
  Divider,
  Menu,
  MenuContent,
  Option,
} from "./timePicker.styles";
import { toTwoDigits } from "./timePicker.utils";

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from({ length: 60 }, (_, index) => index);

export interface TimePickerMenuProps {
  isOpen: boolean;
  size: TimePickerSize;
  zIndex: number;
  position: TimePickerMenuPosition | null;
  currentTime: TimeParts | null;
  menuRef: React.RefObject<HTMLDivElement>;
  onSelectHour: (hours: number) => void;
  onSelectMinute: (minutes: number) => void;
}

/** Renders the hour/minute dropdown menu. */
export const TimePickerMenu = (
  props: TimePickerMenuProps,
): React.ReactElement => {
  const selectedHourRef = useRef<HTMLDivElement | null>(null);
  const selectedMinuteRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!props.isOpen) return;
    selectedHourRef.current?.scrollIntoView({ block: "center" });
    selectedMinuteRef.current?.scrollIntoView({ block: "center" });
  }, [props.isOpen, props.currentTime?.hours, props.currentTime?.minutes]);

  return (
    <Menu
      ref={props.menuRef}
      role="listbox"
      $isOpen={props.isOpen}
      $zIndex={props.zIndex}
      $size={props.size}
      $position={props.position}
    >
      <MenuContent $size={props.size}>
        <Column>
          <ColumnScroll>
            {HOURS.map((hour) => {
              const isSelected = props.currentTime?.hours === hour;
              return (
                <Option
                  key={`hour-${hour}`}
                  role="option"
                  aria-selected={isSelected}
                  $isSelected={isSelected}
                  $size={props.size}
                  ref={(node) => {
                    if (isSelected) selectedHourRef.current = node;
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => props.onSelectHour(hour)}
                >
                  {toTwoDigits(hour)}
                </Option>
              );
            })}
          </ColumnScroll>
        </Column>
        <Divider />
        <Column>
          <ColumnScroll>
            {MINUTES.map((minute) => {
              const isSelected = props.currentTime?.minutes === minute;
              return (
                <Option
                  key={`minute-${minute}`}
                  role="option"
                  aria-selected={isSelected}
                  $isSelected={isSelected}
                  $size={props.size}
                  ref={(node) => {
                    if (isSelected) selectedMinuteRef.current = node;
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => props.onSelectMinute(minute)}
                >
                  {toTwoDigits(minute)}
                </Option>
              );
            })}
          </ColumnScroll>
        </Column>
      </MenuContent>
    </Menu>
  );
};
